// Offline-first store. RULE: capture field writes locally FIRST, always.
// A contact is written to the `outbox` table immediately; a background flush
// pushes it to Supabase when connectivity allows. The door UI (next session)
// will build on this; here we ship the store + a minimal, tested flush path.

import Dexie, { type Table } from 'dexie';
import type { Candidate, Contact, District, Household, StreetSegment, Turf } from './types';
import { supabase } from '../supabaseClient';

/** A GPS-created household (from "Knock here"), awaiting sync. Synced BEFORE its contact. */
export interface HouseholdPayload {
  id: string; // client-generated; the contact references this id
  district_id: string | null;
  street_segment_id: string | null;
  lat: number | null;
  lng: number | null;
  address: string | null;
  address_resolved: string | null;
  is_multi_unit: boolean;
  unit_count: number | null;
  supporters_found: number | null;
  created_by: string;
  created_at: string;
}
export interface HouseholdOp {
  localId?: number;
  payload: HouseholdPayload;
  syncedAt: string | null;
  error?: string | null;
  permanent?: boolean; // server rejected it (e.g. RLS) — stop retrying, surface instead
}

/** Postgres codes that won't fix themselves on retry (RLS, constraint, bad input). */
function isPermanent(code?: string): boolean {
  if (!code) return false;
  return code === '42501' || code.startsWith('23') || code.startsWith('22') || code === 'P0001';
}

/** The exact payload we INSERT into public.contacts (also kept for offline display). */
export interface ContactPayload {
  // Client-generated idempotency key. A retry after a lost ack upserts to the
  // same row (unique index contacts_client_uuid_key) instead of duplicating.
  client_uuid: string;
  household_id: string;
  canvasser_id: string;
  support_level: number | null;
  disposition: string | null;
  candidate_tags: unknown;
  issue_tags: unknown;
  wants_sign: boolean;
  can_volunteer: boolean;
  consent_logged: boolean;
  consent_version: string | null;
  note: string | null;
  geo_lat: number | null;
  geo_lng: number | null;
  created_at: string;
}

/** A contact captured offline, awaiting sync. */
export interface OutboxContact {
  localId?: number;
  payload: ContactPayload;
  createdAt: string;
  syncedAt: string | null;
  error?: string | null;
  permanent?: boolean;
}

/** A household safety/PIPA flag change captured offline, awaiting sync. */
export interface FlagOp {
  localId?: number;
  householdId: string;
  do_not_contact: boolean | null;
  flagged_unsafe: boolean | null;
  createdAt: string;
  syncedAt: string | null;
  error?: string | null;
}

/** Patchable household columns (multi-unit counts + resolved-address label). Note:
 *  `address` is deliberately excluded — it owns the import match-key (address_norm)
 *  and a PIPA CHECK; the UI edits address_resolved instead. */
export type HouseholdPatchFields = Partial<
  Pick<Household, 'is_multi_unit' | 'unit_count' | 'supporters_found' | 'address_resolved'>
>;

/** An arbitrary household field edit captured offline, replayed via patch_household. */
export interface HouseholdPatchOp {
  localId?: number;
  householdId: string;
  fields: HouseholdPatchFields;
  createdAt: string;
  syncedAt: string | null;
  error?: string | null;
  permanent?: boolean; // server rejected it (e.g. RLS) — stop retrying, surface instead
}

export class CanvassDB extends Dexie {
  households!: Table<Household, string>;
  turf!: Table<Turf, string>;
  contacts!: Table<Contact, string>; // server snapshot for offline display
  candidates!: Table<Candidate, string>;
  districts!: Table<District, string>;
  streetSegments!: Table<StreetSegment, string>;
  outbox!: Table<OutboxContact, number>;
  flagQueue!: Table<FlagOp, number>;
  householdOutbox!: Table<HouseholdOp, number>;
  householdPatchQueue!: Table<HouseholdPatchOp, number>;

  constructor() {
    super('victoria-canvass');
    this.version(1).stores({
      households: 'id, turf_id, on_elector_list, do_not_contact',
      turf: 'id, assigned_to, status',
      outbox: '++localId, syncedAt, createdAt',
    });
    this.version(2).stores({
      households: 'id, turf_id, on_elector_list, do_not_contact',
      turf: 'id, assigned_to, status',
      contacts: 'id, household_id, created_at',
      candidates: 'id, nation_slug',
      outbox: '++localId, syncedAt, createdAt',
    });
    this.version(3).stores({
      households: 'id, turf_id, on_elector_list, do_not_contact',
      turf: 'id, assigned_to, status',
      contacts: 'id, household_id, created_at',
      candidates: 'id, nation_slug',
      outbox: '++localId, syncedAt, createdAt',
      flagQueue: '++localId, householdId, syncedAt',
    });
    this.version(4).stores({
      households: 'id, turf_id, district_id, street_segment_id, do_not_contact',
      turf: 'id, assigned_to, status',
      contacts: 'id, household_id, created_at',
      candidates: 'id, nation_slug',
      districts: 'id, assigned_team',
      streetSegments: 'id, district_id, status',
      outbox: '++localId, syncedAt, createdAt',
      flagQueue: '++localId, householdId, syncedAt',
      householdOutbox: '++localId, syncedAt',
    });
    this.version(5).stores({
      households: 'id, turf_id, district_id, street_segment_id, do_not_contact',
      turf: 'id, assigned_to, status',
      contacts: 'id, household_id, created_at',
      candidates: 'id, nation_slug',
      districts: 'id, assigned_team',
      streetSegments: 'id, district_id, status',
      outbox: '++localId, syncedAt, createdAt',
      flagQueue: '++localId, householdId, syncedAt',
      householdOutbox: '++localId, syncedAt',
      householdPatchQueue: '++localId, householdId, syncedAt',
    });
  }
}

export const db = new CanvassDB();

/** Write-local-first: enqueue a contact for later sync. Never blocks on network. */
export async function enqueueContact(
  input: Omit<ContactPayload, 'client_uuid' | 'created_at'> & { client_uuid?: string; created_at?: string },
): Promise<OutboxContact> {
  const now = new Date().toISOString();
  const payload: ContactPayload = {
    ...input,
    client_uuid: input.client_uuid ?? crypto.randomUUID(),
    created_at: input.created_at ?? now,
  };
  const localId = await db.outbox.add({ payload, createdAt: now, syncedAt: null, error: null });
  return { localId, payload, createdAt: now, syncedAt: null, error: null };
}


/** Cache the server contact snapshot for offline display. */
export async function cacheContacts(contacts: Contact[]): Promise<void> {
  if (contacts.length) await db.contacts.bulkPut(contacts);
}
export async function getCachedContacts(): Promise<Contact[]> {
  return db.contacts.toArray();
}
export async function cacheCandidates(candidates: Candidate[]): Promise<void> {
  if (candidates.length) await db.candidates.bulkPut(candidates);
}
export async function getCachedCandidates(): Promise<Candidate[]> {
  return db.candidates.toArray();
}
export async function getCachedHouseholds(): Promise<Household[]> {
  return db.households.toArray();
}
export async function getCachedTurf(): Promise<Turf[]> {
  return db.turf.toArray();
}

// Only one flush runs at a time; flushAgain forces a trailing re-run so an event
// (e.g. `online`) arriving mid-flush is never silently dropped.
let flushing = false;
let flushAgain = false;

/**
 * Push every unsynced outbox row (contacts + flags) to Supabase. Returns counts.
 * Idempotent end-to-end: contacts upsert on client_uuid; set_household_flags is
 * monotonic. Safe to call repeatedly.
 */
export async function flushOutbox(): Promise<{ synced: number; failed: number }> {
  if (flushing) {
    flushAgain = true;
    return { synced: 0, failed: 0 };
  }
  flushing = true;
  let synced = 0;
  let failed = 0;
  try {
    do {
      flushAgain = false;
      // Households created by "Knock here" must sync BEFORE their contacts (FK).
      const hh = await db.householdOutbox.filter((r) => r.syncedAt === null && !r.permanent).toArray();
      const blockedHouseholds = new Set<string>(); // households that failed → hold their contacts
      for (const h of hh) {
        const { error } = await supabase.from('households').upsert(h.payload, { onConflict: 'id', ignoreDuplicates: true });
        if (error) {
          failed += 1;
          blockedHouseholds.add(h.payload.id);
          await db.householdOutbox.update(h.localId!, { error: error.message, permanent: isPermanent(error.code) });
        } else {
          synced += 1;
          await db.householdOutbox.update(h.localId!, { syncedAt: new Date().toISOString(), error: null });
        }
      }
      // also hold contacts whose household is still pending (FK would fail anyway)
      for (const r of await db.householdOutbox.filter((r) => r.syncedAt === null).toArray()) blockedHouseholds.add(r.payload.id);
      const pending = await db.outbox.filter((r) => r.syncedAt === null && !r.permanent).toArray();
      for (const row of pending) {
        if (blockedHouseholds.has(row.payload.household_id)) continue; // wait for the household to sync
        const { error } = await supabase
          .from('contacts')
          .upsert(row.payload, { onConflict: 'client_uuid', ignoreDuplicates: true });
        if (error) {
          failed += 1;
          await db.outbox.update(row.localId!, { error: error.message, permanent: isPermanent(error.code) });
        } else {
          synced += 1;
          await db.outbox.update(row.localId!, { syncedAt: new Date().toISOString(), error: null });
        }
      }
      const flags = await db.flagQueue.filter((r) => r.syncedAt === null).toArray();
      for (const f of flags) {
        const { error } = await supabase.rpc('set_household_flags', {
          p_household_id: f.householdId,
          p_do_not_contact: f.do_not_contact,
          p_flagged_unsafe: f.flagged_unsafe,
        });
        if (error) {
          failed += 1;
          await db.flagQueue.update(f.localId!, { error: error.message });
        } else {
          synced += 1;
          await db.flagQueue.update(f.localId!, { syncedAt: new Date().toISOString(), error: null });
        }
      }
      // Household field edits (multi-unit counts, address). The RPC authorizes the
      // caller and is monotonic; a patch whose household is still pending will
      // succeed on a later flush (households sync first, above).
      const patches = await db.householdPatchQueue.filter((r) => r.syncedAt === null && !r.permanent).toArray();
      for (const p of patches) {
        if (blockedHouseholds.has(p.householdId)) continue; // its household hasn't synced yet
        const { error } = await supabase.rpc('patch_household', { p_household_id: p.householdId, p_patch: p.fields });
        if (error) {
          failed += 1;
          await db.householdPatchQueue.update(p.localId!, { error: error.message, permanent: isPermanent(error.code) });
        } else {
          synced += 1;
          await db.householdPatchQueue.update(p.localId!, { syncedAt: new Date().toISOString(), error: null });
        }
      }
    } while (flushAgain);
  } finally {
    flushing = false;
  }
  return { synced, failed };
}

/** ALL outbox payloads (synced + pending) so a just-saved door stays "visited"
 *  until the server snapshot confirms it. Dedup downstream by client_uuid. */
export async function allPayloads(): Promise<ContactPayload[]> {
  return (await db.outbox.toArray()).map((r) => r.payload);
}

/** Records still waiting to sync (households + contacts + flags + patches) — status strip. */
export async function queuedCount(): Promise<number> {
  const c = await db.outbox.filter((r) => r.syncedAt === null).count();
  const f = await db.flagQueue.filter((r) => r.syncedAt === null).count();
  const h = await db.householdOutbox.filter((r) => r.syncedAt === null).count();
  const p = await db.householdPatchQueue.filter((r) => r.syncedAt === null && !r.permanent).count();
  return c + f + h + p;
}

/** Write-local-first: enqueue a GPS-created household before any network call. */
export async function enqueueHousehold(payload: HouseholdPayload): Promise<void> {
  await db.householdOutbox.add({ payload, syncedAt: null, error: null });
}

// Full-replace: districts/streets are server-authoritative (no local-only rows),
// so a re-import that drops or renumbers them mustn't leave orphans in the cache.
export async function cacheDistricts(d: District[]): Promise<void> {
  await db.transaction('rw', db.districts, async () => {
    await db.districts.clear();
    if (d.length) await db.districts.bulkPut(d);
  });
}
export async function getCachedDistricts(): Promise<District[]> {
  return db.districts.toArray();
}
export async function cacheStreets(s: StreetSegment[]): Promise<void> {
  await db.transaction('rw', db.streetSegments, async () => {
    await db.streetSegments.clear();
    if (s.length) await db.streetSegments.bulkPut(s);
  });
}

/** Reconcile the offline household cache against the authoritative server set:
 *  drop cached rows the server no longer returns (e.g. after a re-import or
 *  --reset renumbers ids) so they don't resurface offline as orphan pins — but
 *  KEEP rows still unsynced in the local household outbox (a just-knocked GPS pin
 *  not yet pushed). Then upsert the server rows. */
export async function reconcileHouseholds(serverRows: Household[]): Promise<void> {
  await db.transaction('rw', db.households, db.householdOutbox, async () => {
    const serverIds = new Set(serverRows.map((h) => h.id));
    const keep = new Set(
      (await db.householdOutbox.filter((r) => r.syncedAt === null).toArray()).map((r) => r.payload.id),
    );
    const cachedIds = (await db.households.toCollection().primaryKeys()) as string[];
    const stale = cachedIds.filter((id) => !serverIds.has(id) && !keep.has(id));
    if (stale.length) await db.households.bulkDelete(stale);
    if (serverRows.length) await db.households.bulkPut(serverRows);
  });
}
export async function getCachedStreets(): Promise<StreetSegment[]> {
  return db.streetSegments.toArray();
}

/** Drop synced rows once the server snapshot includes them — bounds IndexedDB growth. */
export async function pruneSyncedOutbox(confirmed: Set<string>, households: Household[]): Promise<void> {
  const confirmedHouseholds = new Set(households.map((h) => h.id));
  const done = await db.outbox.filter((r) => r.syncedAt !== null && confirmed.has(r.payload.client_uuid)).toArray();
  if (done.length) await db.outbox.bulkDelete(done.map((r) => r.localId!));
  const flags = await db.flagQueue.filter((r) => r.syncedAt !== null).toArray();
  if (flags.length) await db.flagQueue.bulkDelete(flags.map((r) => r.localId!));
  const hh = await db.householdOutbox.filter((r) => r.syncedAt !== null && confirmedHouseholds.has(r.payload.id)).toArray();
  if (hh.length) await db.householdOutbox.bulkDelete(hh.map((r) => r.localId!));
  const drop = patchesToDrop(await db.householdPatchQueue.toArray(), confirmedHouseholds);
  if (drop.length) await db.householdPatchQueue.bulkDelete(drop);
}

/**
 * Pure decision (exported for testing): which household-patch rows to delete given
 * the ids of households present in the current server snapshot. Mirrors how
 * householdOutbox is pruned:
 *  - A permanently-rejected patch is always dropped (neither leak nor overlay a
 *    value the server refused).
 *  - An unsynced patch is kept (it still needs to flush, and it is the only thing
 *    overlaid — so the user's pending edit shows until it syncs).
 *  - A synced patch is dropped once its household appears in the snapshot (the RPC
 *    applied it server-side; the snapshot is now the source of truth). Synced
 *    patches are not overlaid, so this is purely bounding IndexedDB growth.
 */
export function patchesToDrop(patchRows: HouseholdPatchOp[], confirmedHouseholds: Set<string>): number[] {
  return patchRows
    .filter((p) => p.permanent || (p.syncedAt !== null && confirmedHouseholds.has(p.householdId)))
    .map((p) => p.localId!);
}

/** Unsynced GPS households, reconstructed as Household rows to overlay on the map
 *  so a knocked pin survives a refresh/force-quit before it syncs. */
export async function pendingHouseholds(): Promise<Household[]> {
  const rows = await db.householdOutbox.filter((r) => r.syncedAt === null).toArray();
  const out: Household[] = [];
  for (const r of rows) {
    const h = await db.households.get(r.payload.id);
    if (h) out.push(h);
  }
  return out;
}

/** Patch an unsynced GPS household (e.g. add the reverse-geocoded address) so it
 *  syncs with the new value, and update the local cache for immediate display. */
export async function patchPendingHousehold(id: string, fields: Partial<HouseholdPayload>): Promise<void> {
  const rows = await db.householdOutbox.filter((r) => r.syncedAt === null && r.payload.id === id).toArray();
  for (const r of rows) await db.householdOutbox.update(r.localId!, { payload: { ...r.payload, ...fields } });
  await db.households.update(id, fields as Partial<Household>).catch(() => {});
}

/** Write-local-first flag change: persist intent BEFORE any network call. */
export async function enqueueFlag(
  householdId: string,
  flags: { do_not_contact?: boolean; flagged_unsafe?: boolean },
): Promise<void> {
  await db.flagQueue.add({
    householdId,
    do_not_contact: flags.do_not_contact ?? null,
    flagged_unsafe: flags.flagged_unsafe ?? null,
    createdAt: new Date().toISOString(),
    syncedAt: null,
    error: null,
  });
}

/** Pending flag ops, to overlay on the snapshot so a flag survives refresh/force-quit. */
export async function pendingFlags(): Promise<FlagOp[]> {
  return db.flagQueue.filter((r) => r.syncedAt === null).toArray();
}

/** Write-local-first household field edit: persist intent BEFORE any network call.
 *  Replayed via the authorized patch_household RPC; works for ANY household
 *  (imported, synced GPS, or teammate's) — not just unsynced local pins. */
export async function enqueueHouseholdPatch(householdId: string, fields: HouseholdPatchFields): Promise<void> {
  await db.householdPatchQueue.add({
    householdId,
    fields,
    createdAt: new Date().toISOString(),
    syncedAt: null,
    error: null,
  });
}

/** UNSYNCED household patches to overlay on the snapshot so an edit survives a
 *  refresh before it syncs. Once a patch syncs the SERVER is authoritative (a
 *  teammate may have since changed the same field under last-write-wins), so synced
 *  patches are NOT overlaid — that would mask the teammate's value indefinitely.
 *  Permanently-rejected patches are excluded so the UI never shows a refused value. */
export async function pendingHouseholdPatches(): Promise<HouseholdPatchOp[]> {
  return db.householdPatchQueue.filter((r) => r.syncedAt === null && !r.permanent).toArray();
}

/** Cache the households/turf the canvasser will work offline. */
export async function cacheTurfData(turf: Turf[], households: Household[]): Promise<void> {
  await db.transaction('rw', db.turf, db.households, async () => {
    if (turf.length) await db.turf.bulkPut(turf);
    if (households.length) await db.households.bulkPut(households);
  });
}
